import React, { useState, useEffect } from "react";
import { useWallet } from "../../contexts/WalletContext";
import { getPortfolio } from "../../services/trading-api";

const PortfolioPanel = () => {
  const { publicKey, connected } = useWallet();
  const [portfolio, setPortfolio] = useState({
    open_positions: [],
    closed_positions: [],
    fills: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authenticatedUser, setAuthenticatedUser] = useState(null);

  // Check for authenticated user session
  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000'}/api/auth/session`, {
          method: "GET",
          credentials: 'include',
          headers: { "Content-Type": "application/json" },
        });

        const data = await response.json();
        if (response.ok && data.success && data.authenticated) {
          setAuthenticatedUser(data.user);
        } else {
          setAuthenticatedUser(null);
        }
      } catch (err) {
        console.error("Error checking session:", err);
        setAuthenticatedUser(null);
      }
    };
    
    checkSession();
  }, []);

  useEffect(() => {
    // Determine which wallet address to use:
    // 1. If authenticated, use the authenticated user's wallet (from their account)
    // 2. Otherwise, use the connected Phantom wallet
    const walletAddress = authenticatedUser?.wallet_address || (connected && publicKey ? publicKey.toString() : null);
    
    if (!walletAddress) {
      setPortfolio({ open_positions: [], closed_positions: [], fills: [] });
      return;
    }

    const fetchPortfolio = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getPortfolio(walletAddress);
        setPortfolio(data);
      } catch (err) {
        console.error("Error fetching portfolio:", err);
        setError("Failed to load portfolio");
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolio();
    const interval = setInterval(fetchPortfolio, 10000); // Update every 10 seconds
    return () => clearInterval(interval);
  }, [connected, publicKey, authenticatedUser]);

  // Show portfolio if authenticated (uses account wallet) OR if wallet connected (uses Phantom wallet)
  const walletAddress = authenticatedUser?.wallet_address || (connected && publicKey ? publicKey.toString() : null);

  if (!authenticatedUser && !connected) {
    return (
      <div className="p-4 text-center text-white/60">
        <p className="text-sm">Sign in or connect wallet to view portfolio</p>
      </div>
    );
  }

  const formatNumber = (num) => {
    if (!num) return "0";
    const n = parseFloat(num);
    if (n >= 1e9) return (n / 1e9).toFixed(2) + "B";
    if (n >= 1e6) return (n / 1e6).toFixed(2) + "M";
    if (n >= 1e3) return (n / 1e3).toFixed(2) + "K";
    return n.toFixed(4);
  };

  const formatAddress = (address) => {
    if (!address) return "";
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  const totalPnL = portfolio.open_positions.reduce((sum, pos) => {
    return sum + parseFloat(pos.pnl_sol || 0);
  }, 0);

  const totalPnLPct = portfolio.open_positions.length > 0
    ? portfolio.open_positions.reduce((sum, pos) => sum + parseFloat(pos.pnl_pct || 0), 0) / portfolio.open_positions.length
    : 0;

  return (
    <div className="p-4 space-y-4">
      <div className="border-b border-white/20 pb-3">
        <h2 className="text-lg font-bold text-white mb-2">Portfolio</h2>
        <div className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-white/60">Total PnL:</span>
            <span className={totalPnL >= 0 ? "text-green-400" : "text-red-400"}>
              {totalPnL.toFixed(4)} SOL ({totalPnLPct.toFixed(2)}%)
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-white/60">Open Positions:</span>
            <span className="text-white">{portfolio.open_positions.length}</span>
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-center text-white/60 py-4">
          <p className="text-sm">Loading...</p>
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-500/20 border border-red-500/50 rounded text-sm text-red-200">
          {error}
        </div>
      )}

      {/* Open Positions */}
      {portfolio.open_positions.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-white/80 mb-2">Open Positions</h3>
          <div className="space-y-2">
            {portfolio.open_positions.map((position) => (
              <div
                key={position.id}
                className="p-3 bg-white/5 rounded-lg border border-white/10"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="text-xs font-medium text-white">
                      {formatAddress(position.mint)}
                    </div>
                    <div className="text-xs text-white/60 mt-1">
                      Entry: {parseFloat(position.entry_price || 0).toFixed(8)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-medium ${
                      parseFloat(position.pnl_pct || 0) >= 0 ? "text-green-400" : "text-red-400"
                    }`}>
                      {parseFloat(position.pnl_pct || 0).toFixed(2)}%
                    </div>
                    <div className="text-xs text-white/60">
                      {parseFloat(position.pnl_sol || 0).toFixed(4)} SOL
                    </div>
                  </div>
                </div>
                <div className="flex justify-between text-xs text-white/60">
                  <span>Tokens: {formatNumber(parseFloat(position.token_amount || 0))}</span>
                  <span>Size: {parseFloat(position.size_sol || 0).toFixed(4)} SOL</span>
                </div>
                {position.entry_tx_signature && (
                  <a
                    href={`https://solscan.io/tx/${position.entry_tx_signature}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-400 hover:text-blue-300 mt-2 block"
                  >
                    View Entry TX →
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trade History */}
      {portfolio.fills.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-white/80 mb-2">Recent Trades</h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {portfolio.fills.slice(0, 10).map((fill) => (
              <div
                key={fill.id}
                className="p-2 bg-white/5 rounded border border-white/10 text-xs"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <span className={`font-medium ${
                      fill.side === 'BUY' ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {fill.side}
                    </span>
                    <span className="text-white/60 ml-2">
                      {formatNumber(parseFloat(fill.token_amount || 0))} @ {parseFloat(fill.price || 0).toFixed(8)}
                    </span>
                  </div>
                  <div className="text-white/60">
                    {parseFloat(fill.size_sol || 0).toFixed(4)} SOL
                  </div>
                </div>
                {fill.tx_signature && (
                  <a
                    href={`https://solscan.io/tx/${fill.tx_signature}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 mt-1 block"
                  >
                    View TX →
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && portfolio.open_positions.length === 0 && portfolio.fills.length === 0 && (
        <div className="text-center text-white/60 py-8">
          <p className="text-sm">No positions or trades yet</p>
        </div>
      )}
    </div>
  );
};

export default PortfolioPanel;

